import {Button, Modal} from 'components/ui';
import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate, useLocation} from 'react-router-dom';
import LoginBanner from 'assets/images/login/banner.webp';
import {RecoverPasswordService} from 'store/services/authenticationService';
import {getURLParams} from 'utils/functions/getURLParams';
import InputMessage from 'components/InputMessage';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {connect} from 'react-redux';
import {ApplicationState} from 'store/rootReducer';
import {
  fetchTermsRequest,
  getTerms,
  getTermsRequestStatus,
} from 'store/reducers/terms';
import {PasswordInput} from 'letrus-ui';
import styles from './RecoverPassword.module.scss';

function RecoverPassword(): JSX.Element {
  const [passwordRecoverStatus, setPasswordRecoverStatus] =
    useState<string>('');
  const [loadingPasswordRecover, setLoadingPasswordRecover] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const {pathname} = useLocation();

  const {trackEvent} = useTrackEvent();

  const {
    handleSubmit,
    watch,
    formState: {errors},
    register,
  } = useForm({
    mode: 'onChange',
  });

  const password = watch('password');

  const {uidb64, token} = getURLParams(pathname);

  async function handlePasswordRecover(data: {
    password: string;
    confirmPassword: string;
  }) {
    setLoadingPasswordRecover(true);
    trackEvent('password_reset_attempt');

    try {
      const recoverPasswordResponse = await RecoverPasswordService({
        uidb64,
        token,
        new_password1: data.password,
        new_password2: data.confirmPassword,
      });

      const recoverPasswordStatusCode = recoverPasswordResponse?.status;

      setLoadingPasswordRecover(false);

      if (recoverPasswordStatusCode === 200) {
        setPasswordRecoverStatus('success');
      }
    } catch (error) {
      setLoadingPasswordRecover(false);
      setPasswordRecoverStatus('error');
    }
  }

  function redirectToLogin() {
    navigate('/aceite_termos');

    trackEvent('password_reset_login_success', {
      time: {
        spent_time: new Date().getTime(),
      },
    });
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <article className={styles.bannerContent}>
          <img src={LoginBanner} alt="Banner Login" />
        </article>

        <article className={styles.formContent}>
          <h3>Criar uma Nova Senha</h3>

          <b>Dicas para sua nova senha:</b>
          <ol>
            <li>
              Sua senha <b>obrigatoriamente deve conter mais de 6 caracteres</b>
              .
            </li>
            <li>Inclua caracteres maiúsculos e minúsculos.</li>
            <li>Inclua um ou mais números.</li>
            <li>
              Sua senha <b>não</b> pode conter caracteres estranhos e espaços.
            </li>
            <li>
              Salve sua senha em um local seguro e não compartilhe com
              estranhos! A senha é algo pessoal e individual.
            </li>
          </ol>

          <form onSubmit={handleSubmit(handlePasswordRecover)}>
            <div className={styles.inputPassword}>
              <PasswordInput
                placeholder="Digite sua senha"
                label="Digite sua nova senha"
                errorMessage={errors.password?.message}
                {...register('password', {
                  minLength: {
                    value: 6,
                    message:
                      'Oops... Parece que você não criou uma senha com mais de 6 dígitos. Leia as dicas para deixar sua senha mais segura e tente novamente.',
                  },
                })}
              />
            </div>

            <hr />

            <div className={styles.inputPassword}>
              <PasswordInput
                placeholder="Confirme sua senha"
                label="Digite novamente sua nova senha"
                errorMessage={errors.confirmPassword?.message}
                {...register('confirmPassword', {
                  validate: (value) =>
                    value === password ||
                    'Oops... As senhas não são a mesma. Clique para visualizar as senhas, reescreva e tente novamente.',
                })}
              />
            </div>

            <hr />

            {passwordRecoverStatus === 'error' && (
              <InputMessage
                alertType="warningMessage"
                message="Oops... Parece que o tempo para recuperar a senha expirou. Clique em “Esqueci a senha” e tente novamente."
              />
            )}

            <div className={styles.buttonsContainer}>
              <Button
                variant="primary"
                text="Redefinir Senha"
                type="submit"
                textColor="#40357C"
                borderColor="transparent"
                leftIcon={{
                  icon: ['fad', 'check'],
                  color: '#40357C',
                }}
                loading={loadingPasswordRecover}
              />
              <Button
                variant="secondary"
                text="Voltar"
                onClick={() => navigate('/')}
                leftIcon={{
                  icon: ['fad', 'arrow-left'],
                  color: '#40357C',
                }}
              />
            </div>
          </form>
        </article>
      </div>

      <Modal
        onClose={() => {}}
        buttonClose={false}
        isOpen={passwordRecoverStatus === 'success'}
        clickOutsideToClose={false}
        title=""
      >
        <div className={styles.successModal}>
          <h4>Sua senha foi redefinida</h4>

          <p>
            Guarde bem sua nova senha!
            <br />
            Agora você já pode acessar a Letrus e mandar bem na escrita.
          </p>

          <FontAwesomeIcon
            icon={['fad', 'circle-check']}
            color="#2FAC66"
            size="4x"
          />

          <hr />

          <Button
            onClick={() => redirectToLogin()}
            variant="primary"
            text="Entrar como estudante"
            leftIcon={{
              icon: ['fas', 'graduation-cap'],
              color: '#40357C',
            }}
            textColor="#40357C"
          />
        </div>
      </Modal>
    </div>
  );
}

export default connect(
  (state: ApplicationState) => ({
    terms: getTerms(state),
    termsRequestStatus: getTermsRequestStatus(state),
  }),
  {fetchTermsRequest},
)(RecoverPassword);
